import styled from '@emotion/styled'
import React, { ReactNode, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import InfoBlock from '../../components/InfoBlock'

import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import { Theme, useTheme } from '@emotion/react'
import { formatBigMoney, formatInteger, formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits'
import { getLevelName, calcIssueOutcome } from '../utils'
import { EnrichedIssueEntity } from 'types/IssueEntity'

interface IPalette {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

const ChartWrapper = styled.div`
    height: 200px;
    position: relative;
    margin: 20px;
`

const DoughnutInner = styled.div`
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    margin-top: -22px;
    text-align: center;
    z-index: 0;
`

function EmptyBox({ color, side = null }) {
    const sideLength = side || 30
    return <div style={{ width: sideLength, height: sideLength, backgroundColor: color }}></div>
}

export const IssueOutcomeOverview = ({ issue }: { issue: EnrichedIssueEntity }) => {
    const [showDetailedGuarantorView, setShowDetailedGuarantorView] = useState(false)
    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme() as Theme & IPalette

    let content: ReactNode | null = null

    const colors = {
        pctSubscribedWithRights: theme.palette.success.main,
        pctSubscribedWithoutRights: theme.palette.success.light,
        pctSubscribedGuarantors: theme.palette.warning.main,
        pctUnsubscribed: theme.palette.grey[200],
    }

    if (issue) {
        const outcome = calcIssueOutcome(issue)

        const data = {
            labels: [
                t('pages.issues.outcome.chartWithRights'),
                t('pages.issues.outcome.chartWithoutRights'),
                t('pages.issues.outcome.chartByGuarantors'),
                t('pages.issues.outcome.chartNotSubscribed'),
            ],
            datasets: [
                {
                    data: [
                        outcome.pctSubscribedWithRights,
                        outcome.pctSubscribedWithoutRights,
                        outcome.pctSubscribedGuarantors,
                        outcome.pctUnsubscribed,
                    ],
                    backgroundColor: [
                        colors.pctSubscribedWithRights,
                        colors.pctSubscribedWithoutRights,
                        colors.pctSubscribedGuarantors,
                        colors.pctUnsubscribed,
                    ],
                    borderWidth: 1,
                    borderColor: theme.palette.grey[400],
                },
            ],
        }
        const options = {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (item) => formatPercentage(item.parsed, { locale, decimals: 1 }),
                    },
                },
            },
            cutout: '70%',
        }
        const formatSubVolume = (subVolume /* , approx */) => {
            const number = formatInteger(subVolume, { locale })
            return number
            // return `${approx ? 'ca ' : ''}${number}` // don't know if we should show "ca" or not
        }

        const maxFeeLevel = Math.max(...issue.fees.map((level) => level.feeLevel))

        const bodyRows: ReactNode[] = []
        if (outcome.pctSubscribedWithRights > 0 || outcome.unitSubWithRights > 0) {
            bodyRows.push(
                <TableRow key={'pctSubscribedWithRights'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedWithRights} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.withRights')}</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedWithRights, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubWithRights)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubWithRights, { locale })}</TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedWithoutRights > 0 || outcome.unitSubWithoutRights > 0) {
            bodyRows.push(
                <TableRow key={'pctSubscribedWithoutRights'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedWithoutRights} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.withoutRights')}</TableCell>
                    <TableCell>
                        {formatPercentage(outcome.pctSubscribedWithoutRights, { locale, decimals: 0 })}
                    </TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubWithoutRights)}</TableCell>
                    <TableCell>
                        {formatBigMoney(issue.unitSubPrice * outcome.unitSubWithoutRights, { locale })}
                    </TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedGuarantors > 0 || outcome.unitSubGuarantor > 0) {
            bodyRows.push(
                <TableRow
                    key={'pctSubscribedGuarantors'}
                    sx={showDetailedGuarantorView ? { '.MuiTableCell-root': { borderBottom: 'none' } } : null}
                >
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>
                        <Stack direction='column'>
                            <Typography>{t('pages.issues.outcome.byGuarantors')}</Typography>
                            {outcome.detailedGuarantorData.length > 1 ? (
                                <Button
                                    onClick={() => setShowDetailedGuarantorView(!showDetailedGuarantorView)}
                                    sx={{ padding: 0, justifyContent: 'left', textAlign: 'left' }}
                                >
                                    {showDetailedGuarantorView
                                        ? t('site.actions.hideDetailed')
                                        : t('site.actions.showDetailed')}
                                </Button>
                            ) : null}
                        </Stack>
                    </TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubGuarantor, { locale })}</TableCell>
                </TableRow>,
            )

            if (showDetailedGuarantorView && outcome.detailedGuarantorData.length > 1) {
                bodyRows.push(
                    ...outcome.detailedGuarantorData.map((level, index) => (
                        <TableRow
                            key={level.feeLevel}
                            sx={
                                outcome.detailedGuarantorData.length - 1 !== index
                                    ? { td: { borderBottom: 'none', paddingY: 0 } }
                                    : { td: { paddingTop: 0 } }
                            }
                        >
                            <TableCell />
                            <TableCell>{getLevelName(level.feeLevel, maxFeeLevel)}</TableCell>
                            <TableCell>{formatPercentage(level.pctOfIssue * 100, { locale, decimals: 0 })}</TableCell>
                            <TableCell>{formatSubVolume(level.numberOfUnits)}</TableCell>
                            <TableCell>
                                {formatBigMoney(level.amount, { locale })} (
                                {formatPercentage(level.pctOfGuarantSub * 100, { locale, decimals: 0 })})
                            </TableCell>
                        </TableRow>
                    )),
                )
            }
        } else if (!issue.noGuarantors) {
            bodyRows.push(
                <TableRow key={'pctSubscribedGuarantors'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>{t('pages.issues.outcome.byGuarantors')}</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>-</TableCell>
                </TableRow>,
            )
        }

        if (issue.overAllotmentSize > 0 || issue.overAllotmentSizeSecondary > 0) {
            const outcomeNotReady =
                issue.overAllotmentAllocation === null && issue.overAllotmentAllocationSecondary === null
            bodyRows.push(
                <TableRow key={'overAllotment'}>
                    <TableCell />
                    <TableCell>{t('pages.issues.outcome.overAllotment')}</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>{outcomeNotReady ? '-' : formatSubVolume(outcome.overAllotment)}</TableCell>
                    <TableCell>
                        {outcome.overAllotment === 0 || outcomeNotReady
                            ? '-'
                            : formatBigMoney(issue.unitSubPrice * outcome.overAllotment, { locale })}
                    </TableCell>
                </TableRow>,
            )
        }

        content = (
            <>
                <ChartWrapper>
                    <DoughnutInner>
                        <Typography variant='h4'>
                            {formatPercentage(100 - outcome.pctUnsubscribed, { locale, decimals: 0 })}
                        </Typography>
                        <Typography variant='caption'>{t('pages.issues.outcome.subscriptionRate')}</Typography>
                    </DoughnutInner>
                    <Doughnut data={data} options={options} />
                </ChartWrapper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>{t('pages.issues.outcome.subscribed')}</TableCell>
                            <TableCell>{t('pages.issues.outcome.percent')}</TableCell>
                            <TableCell>
                                {sharesOrUnits.isUnit(issue)
                                    ? t('pages.issues.outcome.countUnits')
                                    : t('pages.issues.outcome.countShares')}
                            </TableCell>
                            <TableCell>{t('pages.issues.outcome.amount')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bodyRows}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell></TableCell>
                            <TableCell>{t('site.table.total')}</TableCell>
                            <TableCell>
                                {formatPercentage(
                                    outcome.pctSubscribedWithRights +
                                        outcome.pctSubscribedWithoutRights +
                                        outcome.pctSubscribedGuarantors,
                                    { decimals: 0, locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatInteger(
                                    outcome.unitSubWithRights +
                                        outcome.unitSubWithoutRights +
                                        outcome.unitSubGuarantor +
                                        outcome.overAllotment,
                                    { locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatBigMoney(
                                    issue.unitSubPrice *
                                        (outcome.unitSubWithRights +
                                            outcome.unitSubWithoutRights +
                                            outcome.unitSubGuarantor +
                                            outcome.overAllotment),
                                    { locale },
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {issue.unitSubTotal && (
                    <>
                        <Typography variant='body1' gutterBottom>
                            {t('pages.issues.outcome.totalSubscribed', {
                                quantity: sharesOrUnits.quantityLabel(
                                    issue,
                                    issue.unitSubTotal + outcome.overAllotment,
                                    locale,
                                ),
                                amount: formatBigMoney(
                                    (issue.unitSubTotal + outcome.overAllotment) * issue.unitSubPrice,
                                    {
                                        locale,
                                        currencySymbol: issue.currency,
                                        decimals: 0,
                                    },
                                ),
                            })}
                        </Typography>
                        <Typography variant='body1' gutterBottom>
                            {t('pages.issues.outcome.subscriptionRateBreakdown', {
                                totalRate: formatPercentage(100 * (issue.unitSubTotal / issue.totalOfferedUnits), {
                                    locale,
                                    decimals: 2,
                                }),
                                withRightsRate:
                                    formatPercentage(outcome.pctSubscribedWithRights, { locale, decimals: 2 }) ||
                                    formatPercentage(100 * (outcome.unitSubWithRights / outcome.unitSubTotal), {
                                        locale,
                                        decimals: 2,
                                    }),
                                withoutRightsRate: formatPercentage(
                                    outcome.pctSubscribedWithoutRights ||
                                        outcome.unitSubWithoutRights / outcome.unitSubTotal,
                                    { locale, decimals: 2 },
                                ),
                            })}
                        </Typography>

                        {outcome.pctSubscribedGuarantors > 0 && (
                            <Typography variant='body1' gutterBottom>
                                {t('pages.issues.outcome.guarantorsSubscribed', {
                                    pctOfOffer: formatPercentage(outcome.pctSubscribedGuarantors, {
                                        locale,
                                        decimals: 0,
                                    }),
                                    pctOfIssued: formatPercentage(
                                        (100 * outcome.pctSubscribedGuarantors) /
                                            (outcome.pctSubscribedGuarantors +
                                                outcome.pctSubscribedWithRights +
                                                outcome.pctSubscribedWithoutRights),
                                        { locale, decimals: 0 },
                                    ),
                                    pctOfGuaranteed: formatPercentage(
                                        (outcome.pctSubscribedGuarantors / outcome.pctGuarantorCommitment) * 100,
                                        { locale, decimals: 0 },
                                    ),
                                })}
                            </Typography>
                        )}
                        {issue.overAllotmentSize > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                {t('pages.issues.outcome.overAllotmentUsed', {
                                    maxQuantity: sharesOrUnits.quantityLabel(issue, issue.overAllotmentSize, locale),
                                    usage:
                                        issue.overAllotmentAllocation === 0
                                            ? t('pages.issues.outcome.notUsed')
                                            : t('pages.issues.outcome.usedTo', {
                                                  quantity: sharesOrUnits.quantityLabel(
                                                      issue,
                                                      issue.overAllotmentAllocation,
                                                      locale,
                                                  ),
                                              }),
                                })}
                            </Typography>
                        ) : null}
                        {issue.overAllotmentSizeSecondary > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                {t('pages.issues.outcome.overAllotmentSecondaryUsed', {
                                    maxQuantity: formatNumber(issue.overAllotmentSizeSecondary, {
                                        locale,
                                        decimals: 0,
                                    }),
                                    usage:
                                        issue.overAllotmentAllocationSecondary === 0
                                            ? t('pages.issues.outcome.notUsed')
                                            : t('pages.issues.outcome.usedTo', {
                                                  quantity: sharesOrUnits.quantityLabel(
                                                      issue,
                                                      issue.overAllotmentAllocationSecondary,
                                                      locale,
                                                  ),
                                              }),
                                })}
                            </Typography>
                        ) : null}
                        {issue.secondaryOffering > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                {t('pages.issues.outcome.secondaryOfferingUsed', {
                                    offered: sharesOrUnits.quantityLabel(issue, issue.secondaryOffering, locale),
                                    subscribed: sharesOrUnits.quantityLabel(issue, issue.secondarySub, locale),
                                })}
                            </Typography>
                        ) : null}
                    </>
                )}
            </>
        )
    }
    return (
        <InfoBlock title={t('pages.issues.outcome.title')} loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}
